import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const GH_SVG = (
  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
  </svg>
);

const Terminal = ({ title, children }) => (
  <div className="rounded-xl overflow-hidden bg-[#050810] border border-white/[0.08] shadow-[0_32px_64px_rgba(0,0,0,0.5)]">
    <div className="flex items-center gap-1.5 px-4 py-3 bg-white/[0.03] border-b border-white/[0.06]">
      <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
      <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
      <div className="w-3 h-3 rounded-full bg-[#28c840]" />
      {title && <span className="ml-3 text-[11px] text-gray-600 font-mono tracking-wide">{title}</span>}
    </div>
    <div className="p-6 font-mono text-[12.5px] leading-[1.85]">{children}</div>
  </div>
);

const Tag = ({ children, cls }) => (
  <span className={`inline-block px-2.5 py-0.5 text-[11px] font-mono rounded border ${cls}`}>
    {children}
  </span>
);

const PRIVACY_FEATURES = [
  {
    badge: 'UTXO',
    name: 'Commitment + Nullifier',
    desc: 'Shielded payments via Poseidon-hashed commitments and nullifiers. A 2²⁰-depth Merkle tree tracks unspent outputs on-chain.',
    tag: 'text-violet-400 bg-violet-950/70 border-violet-800/40',
    cardBg: 'bg-violet-950/[0.14] hover:bg-violet-950/[0.22]',
  },
  {
    badge: 'Groth16',
    name: 'ZK Spend Proofs',
    desc: 'Every transfer is proven by a Groth16 circuit: valid Merkle membership, correct nullifier, output commitments — without revealing amount or parties.',
    tag: 'text-indigo-400 bg-indigo-950/70 border-indigo-800/40',
    cardBg: 'bg-indigo-950/[0.14] hover:bg-indigo-950/[0.22]',
  },
  {
    badge: 'BabyJubjub',
    name: 'ZK-Native Keys',
    desc: 'BabyJubjub keypairs are circuit-compatible for key-ownership proofs. Hybrid-encrypted memos deliver payment details only to the receiver.',
    tag: 'text-cyan-400 bg-cyan-950/70 border-cyan-800/40',
    cardBg: 'bg-cyan-950/[0.14] hover:bg-cyan-950/[0.22]',
  },
  {
    badge: 'Audit',
    name: 'Selective Disclosure',
    desc: 'An auditor keypair decrypts amount, asset, and owner from any payment memo — without breaking privacy for anyone else. ZK-provable.',
    tag: 'text-amber-400 bg-amber-950/70 border-amber-800/40',
    cardBg: 'bg-amber-950/[0.12] hover:bg-amber-950/[0.20]',
  },
  {
    badge: 'Freeze',
    name: 'Compliance Controls',
    desc: 'Nullifier-based asset freezing. Frozen commitments cannot be spent. Regulatory compliance without sacrificing on-chain privacy.',
    tag: 'text-rose-400 bg-rose-950/70 border-rose-800/40',
    cardBg: 'bg-rose-950/[0.12] hover:bg-rose-950/[0.20]',
  },
  {
    badge: 'AI',
    name: 'Agent-Friendly',
    desc: 'Payment-focused design — no general programmability. WASM-compiled circuits for browser and autonomous AI agent usage. Cross-chain settlement ready.',
    tag: 'text-emerald-400 bg-emerald-950/70 border-emerald-800/40',
    cardBg: 'bg-emerald-950/[0.12] hover:bg-emerald-950/[0.20]',
  },
];

const LandingPage = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-[#0B1120] text-white overflow-x-hidden">

      {/* ─── Navigation ─── */}
      <header className="sticky top-0 z-50 bg-[#0B1120]/90 backdrop-blur-md border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-nobg.png" alt="ZPayNow" className="w-6 h-6" />
            <span className="font-mono text-[13px] text-gray-400">zpaynow</span>
          </div>
          <nav className="flex items-center gap-1">
            <a
              href="https://github.com/zpaynow"
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-mono text-gray-500 hover:text-white hover:bg-white/5 transition-all"
            >
              {GH_SVG} github
            </a>
            <Link
              to="/platform"
              className="ml-2 px-4 py-1.5 rounded-md text-[12px] font-mono font-semibold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20 hover:border-cyan-500/40 transition-all"
            >
              platform →
            </Link>
          </nav>
        </div>
      </header>

      {/* ─── Hero ─── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.018] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI0ZGRkZGRiIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-violet-500/[0.09] rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute top-1/4 right-0 w-[400px] h-[300px] bg-indigo-500/[0.05] rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 py-24 lg:py-32">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-16 items-start">

            {/* Left */}
            <div>
              <div className="flex flex-wrap gap-2 mb-8">
                {[
                  ['Privacy',   'text-violet-400 bg-violet-950/80 border-violet-800/40'],
                  ['Auditable', 'text-amber-400 bg-amber-950/80 border-amber-800/40'],
                  ['AI-native', 'text-emerald-400 bg-emerald-950/80 border-emerald-800/40'],
                  ['Rust',      'text-rose-400 bg-rose-950/80 border-rose-800/40'],
                ].map(([label, cls]) => (
                  <Tag key={label} cls={cls}>{label}</Tag>
                ))}
              </div>

              <h1 className="text-[4rem] lg:text-[5rem] font-black leading-[0.92] tracking-tight mb-7">
                <span className="text-white">ZPay</span>
                <span className="text-violet-400">Now</span>
              </h1>

              <p className="text-[1.05rem] text-gray-300 leading-[1.75] mb-3 max-w-[480px]">
                Open-source infrastructure for privacy-auditable payments — designed for AI agents, auditors, and the open financial stack.
              </p>
              <p className="text-[0.875rem] text-gray-500 font-mono mb-10">
                github.com/zpaynow · GPL-3.0 · Rust
              </p>

              <div className="flex flex-wrap gap-3">
                <a
                  href="https://github.com/zpaynow"
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/[0.06] border border-white/[0.1] hover:bg-white/[0.1] hover:border-white/20 text-[13px] font-mono text-gray-300 hover:text-white transition-all duration-200"
                >
                  {GH_SVG} zpaynow
                </a>
                <Link
                  to="/platform"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-[13px] font-mono font-bold text-[#080D1A] transition-all duration-200 shadow-lg shadow-cyan-500/20"
                >
                  ZeroPay Platform →
                </Link>
              </div>
            </div>

            {/* Right: two project cards */}
            <div className="space-y-4 animate-float">
              {/* Auditable Privacy - featured */}
              <div className="rounded-xl border border-violet-500/40 bg-gradient-to-br from-violet-950/35 via-[#0B1120] to-[#0B1120] p-6 shadow-[0_0_40px_rgba(139,92,246,0.07)]">
                <div className="flex items-center justify-between mb-4">
                  <Tag cls="text-violet-400 bg-violet-950/70 border-violet-800/40">Auditable-Privacy-Payment</Tag>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-400">in progress</span>
                </div>
                <p className="text-[13px] text-gray-300 leading-[1.7] mb-4">
                  UTXO shielded payments with ZK proofs, selective auditor disclosure, compliance freezing, and AI-agent–native design.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['UTXO', 'Groth16', 'Poseidon', 'BabyJubjub', 'WASM'].map((t) => (
                    <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/[0.04] border border-white/[0.07] text-gray-400">{t}</span>
                  ))}
                </div>
              </div>

              {/* ZeroPay */}
              <div className="rounded-xl border border-cyan-500/30 bg-gradient-to-br from-cyan-950/20 via-[#0B1120] to-[#0B1120] p-6">
                <div className="flex items-center justify-between mb-4">
                  <Tag cls="text-cyan-400 bg-cyan-950/70 border-cyan-800/40">ZeroPay · x402</Tag>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[10px] font-mono text-emerald-400">live</span>
                  </div>
                </div>
                <p className="text-[13px] text-gray-300 leading-[1.7] mb-4">
                  x402/v1 payment facilitator for AI agents. EIP-3009 gasless transfers, EIP-712 signatures, EIP-8004 agent identity.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['x402/v1', 'EIP-3009', 'EIP-712', 'EIP-8004'].map((t) => (
                    <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/[0.04] border border-white/[0.07] text-gray-400">{t}</span>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ─── Auditable Privacy Payment — Deep Dive ─── */}
      <section className="border-t border-violet-500/[0.15] py-24 bg-gradient-to-b from-violet-950/[0.06] to-transparent">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-14">
            <div className="text-[10px] font-mono text-violet-500 uppercase tracking-[0.18em] mb-3">
              Next · Auditable-Privacy-Payment
            </div>
            <h2 className="text-[2rem] font-black text-white">Privacy and auditability.<br />Not a trade-off.</h2>
          </div>

          {/* Feature grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/[0.05] border border-white/[0.05] rounded-xl overflow-hidden mb-16">
            {PRIVACY_FEATURES.map((f) => (
              <div key={f.badge} className={`p-7 transition-colors ${f.cardBg}`}>
                <div className="mb-5"><Tag cls={f.tag}>{f.badge}</Tag></div>
                <h3 className="text-[15px] font-bold text-white mb-2.5">{f.name}</h3>
                <p className="text-[13px] text-gray-400 leading-[1.7]">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Flow + terminal */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h3 className="text-[1rem] font-bold text-white mb-8">Payment flow</h3>
              {[
                {
                  n: '01',
                  title: 'Deposit',
                  accent: 'text-cyan-400',
                  border: 'border-cyan-500/40',
                  desc: 'Commit assets into the shielded pool. A Groth16 proof is generated. The Poseidon commitment is inserted into the Merkle tree.',
                },
                {
                  n: '02',
                  title: 'Transfer',
                  accent: 'text-violet-400',
                  border: 'border-violet-500/40',
                  desc: 'Spend a UTXO: prove Merkle membership, produce the nullifier, create output commitments. Auditor-encrypted and receiver-encrypted memos are attached.',
                },
                {
                  n: '03',
                  title: 'Audit / Withdraw',
                  accent: 'text-amber-400',
                  border: 'border-amber-500/40',
                  desc: 'The auditor decrypts memos with their key for selective disclosure. User withdraws by burning the nullifier and claiming on-chain.',
                },
              ].map((s) => (
                <div key={s.n} className={`flex items-start gap-5 mb-7 pl-5 border-l-2 ${s.border}`}>
                  <span className={`text-[2.4rem] font-black font-mono ${s.accent} opacity-40 leading-none flex-shrink-0 w-10`}>
                    {s.n}
                  </span>
                  <div>
                    <div className="text-[14px] font-bold text-white mb-1.5">{s.title}</div>
                    <p className="text-[13px] text-gray-400 leading-[1.75]">{s.desc}</p>
                  </div>
                </div>
              ))}

              <div className="mt-2 p-4 rounded-lg bg-violet-950/[0.30] border border-violet-500/30 text-[12px] font-mono text-gray-300 leading-[1.8]">
                Groth16 proofs from deposits and transfers are aggregated via{' '}
                <span className="text-violet-400">SnarkFold</span> — collapsing multiple proofs into a constant-size on-chain verification regardless of batch size.
              </div>
            </div>

            <Terminal title="privacy-payment · shielded transfer">
              <div className="text-[10px] text-gray-700 uppercase tracking-[0.15em] mb-1.5">
                // deposit into shielded pool
              </div>
              <div>
                <span className="text-cyan-400">deposit</span>
                <span className="text-gray-500">(asset=</span>
                <span className="text-purple-400">1</span>
                <span className="text-gray-500">, amount=</span>
                <span className="text-purple-400">100_000</span>
                <span className="text-gray-500">)</span>
              </div>
              <div className="text-gray-500 pl-4">
                <span className="text-gray-700">← </span>commitment: <span className="text-emerald-400">0x1a2b...f3c4</span>
              </div>
              <div className="text-gray-500 pl-4">
                <span className="text-gray-700">← </span>merkle_leaf: <span className="text-purple-400">42</span>
              </div>
              <div className="text-gray-500 pl-4">
                <span className="text-gray-700">← </span>proof: <span className="text-emerald-400">Groth16 ✓</span>
              </div>

              <div className="h-4" />

              <div className="text-[10px] text-gray-700 uppercase tracking-[0.15em] mb-1.5">
                // transfer (off-chain proof, on-chain nullifier)
              </div>
              <div>
                <span className="text-violet-400">transfer</span>
                <span className="text-gray-500">(</span>
                <span className="text-amber-400">input</span>
                <span className="text-gray-700">: </span>
                <span className="text-emerald-400">0x1a2b...f3c4</span>
                <span className="text-gray-500">) {'{'}</span>
              </div>
              <div className="text-gray-500 pl-4">
                <span className="text-amber-400">nullifier</span><span className="text-gray-700">: </span><span className="text-emerald-400">poseidon(commit, sk)</span>
              </div>
              <div className="text-gray-500 pl-4">
                <span className="text-amber-400">output</span><span className="text-gray-700">:  </span><span className="text-emerald-400">new_commitment</span>
              </div>
              <div className="text-gray-500 pl-4">
                <span className="text-amber-400">memo</span><span className="text-gray-700">:   </span><span className="text-cyan-400">hybrid_encrypt(receiver)</span>
              </div>
              <div className="text-gray-500 pl-4">
                <span className="text-amber-400">audit</span><span className="text-gray-700">:  </span><span className="text-amber-400">audit_encrypt(auditor_pk)</span>
              </div>
              <div className="text-gray-500">{'}'}</div>

              <div className="h-4" />

              <div className="text-[10px] text-gray-700 uppercase tracking-[0.15em] mb-1.5">
                // auditor selective disclosure
              </div>
              <div>
                <span className="text-amber-400">audit_decrypt</span>
                <span className="text-gray-500">(auditor_sk, memo)</span>
              </div>
              <div className="text-gray-500 pl-4">
                <span className="text-gray-700">← </span>asset: <span className="text-purple-400">1</span>
                <span className="text-gray-700">, </span>amount: <span className="text-purple-400">100_000</span>
              </div>
              <div className="text-gray-500 pl-4">
                <span className="text-gray-700">← </span>owner: <span className="text-emerald-400">BabyJubjub(pk)</span>
              </div>
            </Terminal>
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <a
              href="https://github.com/zpaynow/Auditable-Privacy-Payment"
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-violet-500/10 border border-violet-500/20 hover:bg-violet-500/20 text-[13px] font-mono text-violet-300 hover:text-violet-200 transition-all"
            >
              {GH_SVG} Auditable-Privacy-Payment
            </a>
            <a
              href="mailto:hi@zpaynow.com"
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-violet-500/20 bg-violet-500/[0.06] hover:bg-violet-500/[0.12] hover:border-violet-500/35 text-[13px] font-mono text-violet-300 hover:text-violet-200 transition-all"
            >
              Interested? Come chat →
            </a>
          </div>
        </div>
      </section>

      {/* ─── ZeroPay / Platform CTA ─── */}
      <section className="border-t border-cyan-500/[0.12] py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8">

            {/* Self-host */}
            <div className="rounded-xl border border-cyan-500/[0.15] p-8 bg-gradient-to-br from-cyan-950/[0.12] to-[#0D1220]">
              <div className="text-[10px] font-mono text-cyan-600 uppercase tracking-[0.18em] mb-4">ZeroPay · Open Source</div>
              <h3 className="text-[1.3rem] font-black text-white mb-4">Self-host the x402 facilitator.</h3>
              <p className="text-gray-300 text-[0.9rem] leading-[1.8] mb-6">
                Four REST endpoints. EIP-3009 gasless settlement. Docker in minutes. Full source under GPL-3.0 — audit every line.
              </p>
              <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] mb-6 max-w-fit">
                <span className="text-gray-700 font-mono text-sm select-none">$</span>
                <code className="text-[13px] font-mono text-gray-300 select-all">docker-compose up -d</code>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://github.com/zpaynow/ZeroPay"
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[13px] font-mono text-gray-300 hover:text-white transition-colors"
                >
                  {GH_SVG} View Source
                </a>
                <span className="text-gray-700">·</span>
                <a
                  href="https://github.com/zpaynow/ZeroPay/blob/main/API.md"
                  target="_blank" rel="noopener noreferrer"
                  className="text-[13px] font-mono text-gray-300 hover:text-white transition-colors"
                >
                  API Docs →
                </a>
              </div>
            </div>

            {/* Managed */}
            <div className="relative rounded-xl border border-cyan-500/20 overflow-hidden p-8 bg-gradient-to-br from-cyan-950/[0.15] via-[#0D1220] to-[#0D1220]">
              <div className="absolute top-0 right-0 w-[300px] h-[150px] bg-cyan-500/[0.10] rounded-full blur-[60px] pointer-events-none" />
              <div className="relative z-10">
                <div className="text-[10px] font-mono text-cyan-600 uppercase tracking-[0.18em] mb-4">ZeroPay · Managed</div>
                <h3 className="text-[1.3rem] font-black text-white mb-4">Zero infrastructure overhead.</h3>
                <p className="text-gray-300 text-[0.9rem] leading-[1.8] mb-6">
                  zpaynow.com runs ZeroPay as a managed service. Connect your wallet, get an API key, start accepting x402 payments in minutes.
                </p>
                <Link
                  to="/platform"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-[13px] font-mono font-bold text-[#080D1A] transition-all shadow-lg shadow-cyan-500/20"
                >
                  Open Platform →
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="border-t border-white/[0.06] py-14 bg-gradient-to-b from-violet-950/[0.04] to-transparent">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row justify-between gap-12">

            {/* Brand */}
            <div className="max-w-[220px]">
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/logo-nobg.png" alt="ZPayNow" className="w-5 h-5" />
                <span className="font-mono text-sm text-gray-400">ZPayNow</span>
              </div>
              <p className="text-[12px] text-gray-500 font-mono leading-[1.8]">
                Privacy-auditable payment infrastructure<br />
                Licensed under GPL-3.0<br />
                Built with Rust
              </p>
            </div>

            {/* Links */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-10 text-[12px]">

              <div>
                <div className="text-[10px] font-mono text-gray-500 uppercase tracking-[0.15em] mb-4">Projects</div>
                <div className="space-y-2.5">
                  <a href="https://github.com/zpaynow/Auditable-Privacy-Payment" target="_blank" rel="noopener noreferrer"
                    className="block text-violet-400 hover:text-violet-300 transition-colors font-mono">
                    Auditable-Privacy-Payment
                  </a>
                  <a href="https://github.com/zpaynow/ZeroPay" target="_blank" rel="noopener noreferrer"
                    className="block text-cyan-500 hover:text-cyan-400 transition-colors font-mono">
                    ZeroPay · x402
                  </a>
                  <a href="https://github.com/zpaynow/snarkfold" target="_blank" rel="noopener noreferrer"
                    className="block text-gray-400 hover:text-white transition-colors font-mono">
                    SnarkFold
                  </a>
                </div>
              </div>

              <div>
                <div className="text-[10px] font-mono text-gray-500 uppercase tracking-[0.15em] mb-4">ZeroPay Docs</div>
                <div className="space-y-2.5">
                  {[
                    ['API Reference', 'https://github.com/zpaynow/ZeroPay/blob/main/API.md'],
                    ['AI Integration', 'https://github.com/zpaynow/ZeroPay/blob/main/docs/AI_INTEGRATION_GUIDE.md'],
                    ['Deployment', 'https://github.com/zpaynow/ZeroPay/blob/main/DEPLOYMENT.md'],
                    ['x402 Protocol', 'https://github.com/zpaynow/ZeroPay/blob/main/x402.md'],
                  ].map(([label, href]) => (
                    <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                      className="block text-gray-400 hover:text-white transition-colors font-mono">
                      {label}
                    </a>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[10px] font-mono text-gray-500 uppercase tracking-[0.15em] mb-4">Community</div>
                <div className="space-y-2.5">
                  <a href="https://github.com/zpaynow" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors font-mono">
                    {GH_SVG} GitHub
                  </a>
                  <a href="https://x.com/zpaynow" target="_blank" rel="noopener noreferrer"
                    className="block text-gray-400 hover:text-white transition-colors font-mono">
                    X / Twitter
                  </a>
                  <a href="https://discord.gg/t6hFP5qdd3" target="_blank" rel="noopener noreferrer"
                    className="block text-gray-400 hover:text-white transition-colors font-mono">
                    Discord
                  </a>
                  <a href="mailto:hi@zpaynow.com"
                    className="block text-gray-400 hover:text-white transition-colors font-mono">
                    hi@zpaynow.com
                  </a>
                </div>
              </div>

            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/[0.04] flex flex-col sm:flex-row justify-between items-center gap-2 text-[11px] font-mono text-gray-600">
            <span>© 2025 ZPayNow — Privacy-Auditable Payment Infrastructure</span>
            <span>GPL-3.0 · Free Software</span>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
